import React, { useCallback, useRef, useEffect } from 'react';
import { styled } from '@compiled/react';
import isNil from 'lodash/isNil';
import noop from 'lodash/noop';
import AkForm, { ErrorMessage } from '@atlaskit/form';
import { B200 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useCommonPageContext } from '@atlassian/jira-servicedesk-common-page-context';
import type { Intl } from '@atlassian/jira-shared-types/src/general.tsx';
import {
	CLONE_MODE,
	COPY_MODE,
	OTHER_PROJECT_CALENDARS_EDIT_MODE,
} from '../../../common/constants';
import type { FormValues, SelectOption, SelectOptions, Mode } from '../../../common/types';
import { Footer } from '../../../common/ui/footer/index.tsx';
import { RecalculationWarning } from '../../../common/ui/recalculation-warning-message/main.tsx';
import { useSLAConfigActionsFlags } from '../../../common/ui/sla-config-action-flags';
import { getValidationErrorMessage } from '../../../common/utils';
import { DEFAULT_CALENDAR_DATA } from '../../../common/utils/calendars/index.tsx';
import { useFormContext } from '../../../controllers/form/index.tsx';
import { useRecalculationCostService } from '../../../services/recalculation-cost/index.tsx';
import { useSaveCalendarService } from '../../../services/save-calendar/main.tsx';
import { Holidays } from './fields/holidays/index.tsx';
import { NameField } from './fields/name/index.tsx';
import { TimezoneField } from './fields/timezone/index.tsx';
import { WorkWeek } from './fields/work-week/index.tsx';
import { getSaveRequestPayload } from './form-submission-utils';
import messages from './messages';
import { NavBlocker } from './nav-blocker';

type Props = {
	defaultTimezone: SelectOption;
	allTimezones: SelectOptions;
} & Intl & {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		calendarData?: any;
		calendarMode: Mode;
		onCancel: () => void;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		onEditCalendarSuccessful: (updatedCalendar?: any) => void;
	};

export const CalendarFormView = ({
	calendarData,
	defaultTimezone,
	allTimezones,
	onCancel,
	onEditCalendarSuccessful,
	calendarMode,
	intl: { formatMessage },
}: Props) => {
	const { baseUrl, projectKey } = useCommonPageContext();
	const {
		id,
		name,
		timezoneId,
		holidays: initialHolidays,
		context,
		deletable,
		workingTimes,
		serviceDeskName,
		projectKey: OtherCalendarProjectKey,
	} = calendarData || DEFAULT_CALENDAR_DATA;

	let actualProjectKey: string;
	let calendarNameInForm: string;
	switch (calendarMode) {
		case COPY_MODE:
			calendarNameInForm = formatMessage(messages.otherProjectCalendarsCopy, {
				calendarName: name,
				serviceDeskName,
			});
			// @ts-expect-error(TS2322 baseUrl, projectId or projectKey can be undefined, if usePageContext() returns null. Please handle undefined value in your code) - Type 'string | undefined' is not assignable to type 'string'.
			actualProjectKey = projectKey;
			break;

		case CLONE_MODE:
			calendarNameInForm = formatMessage(messages.currentProjectCalendarsCopy, {
				calendarName: name,
			});
			// @ts-expect-error(TS2322 baseUrl, projectId or projectKey can be undefined, if usePageContext() returns null. Please handle undefined value in your code) - Type 'string | undefined' is not assignable to type 'string'.
			actualProjectKey = projectKey;
			break;

		// @ts-expect-error - TS2678 - Type '"other_project_calendars_edit_mode"' is not comparable to type 'Mode'.
		case OTHER_PROJECT_CALENDARS_EDIT_MODE: // We need to change the projectKey if it does not belong to the current project
			calendarNameInForm = name;
			actualProjectKey = OtherCalendarProjectKey;
			break;

		default:
			calendarNameInForm = name;
			// @ts-expect-error(TS2322 baseUrl, projectId or projectKey can be undefined, if usePageContext() returns null. Please handle undefined value in your code) - Type 'string | undefined' is not assignable to type 'string'.
			actualProjectKey = projectKey;
			break;
	}

	const { calendarSaveFailedFlag } = useSLAConfigActionsFlags();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	// @ts-expect-error(TS2322 baseUrl, projectId or projectKey can be undefined, if usePageContext() returns null. Please handle undefined value in your code) - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
	const [{ loading, error }, { fetch }] = useSaveCalendarService(baseUrl, actualProjectKey);
	const [{ isFormDirty, getNextCalendarActionCallback }] = useFormContext();
	const formRef = useRef<unknown>(null);
	const isCopied: boolean = calendarMode === COPY_MODE || calendarMode === CLONE_MODE;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const holidaysRef = useRef<any>(initialHolidays);

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const setHolidaysRef = (updatedHolidays: any) => {
		holidaysRef.current = updatedHolidays;
	};

	const onSubmit = useCallback(
		async (formData: FormValues) => {
			const holidaysPayload = holidaysRef.current.map(
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				({ name: holidayName, iso8601Date, isRecurring }: any) => ({
					name: holidayName,
					iso8601Date,
					recurring: isRecurring,
				}),
			);

			const payloadWithoutHolidays = getSaveRequestPayload(id, context, formData, isCopied);
			const payload = {
				...payloadWithoutHolidays,
				holidays: holidaysPayload,
			};
			const savedCalendarData = await fetch(payload, calendarMode);
			if (!isNil(savedCalendarData)) {
				onEditCalendarSuccessful({
					...savedCalendarData,
					serviceDeskName,
					projectKey: actualProjectKey,
				});
			} else {
				// Show the warning flag saying the save was unsuccessful
				calendarSaveFailedFlag();
			}
			getNextCalendarActionCallback();
		},
		[
			actualProjectKey,
			calendarMode,
			calendarSaveFailedFlag,
			context,
			fetch,
			getNextCalendarActionCallback,
			holidaysRef,
			id,
			isCopied,
			onEditCalendarSuccessful,
			serviceDeskName,
		],
	);

	const onCancelWithNextCalendarActionCallback = () => {
		onCancel();
		getNextCalendarActionCallback(); // Call the nextCalendarAction callback method
	};

	const saveMetricFromBlockerModal = useCallback(() => {
		if (formRef.current !== null) {
			// https://github.com/final-form/react-final-form/blob/master/docs/faq.md#how-can-i-trigger-a-submit-from-outside-my-form
			// @ts-expect-error - TS2571 - Object is of type 'unknown'.
			formRef.current.dispatchEvent(new Event('submit', { cancelable: true }));
		}
	}, []);

	const [{ data: costData, error: costError, loading: costLoading }, { fetch: fetchCost }] =
		// @ts-expect-error(TS2322 baseUrl, projectId or projectKey can be undefined, if usePageContext() returns null. Please handle undefined value in your code) - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
		useRecalculationCostService(baseUrl, projectKey);

	useEffect(() => {
		const fetchData = async () => {
			if (!costLoading && !costError && !costData) {
				await fetchCost();
			}
		};
		fetchData();
	}, [costData, costError, fetchCost, costLoading]);

	useEffect(() => {
		fireTrackAnalytics(createAnalyticsEvent({}), 'editSlaCalendar viewed');
	}, [createAnalyticsEvent]);

	return (
		<AkForm onSubmit={onSubmit}>
			{({ formProps, dirty, getValues: getFormValues }) => (
				<form
					{...formProps}
					// @ts-expect-error - TS2322 - Type 'MutableRefObject<unknown>' is not assignable to type 'LegacyRef<HTMLFormElement> | undefined'.
					ref={formRef}
					data-testid="servicedesk-sla-settings.ui.calendars.editable.ak-form"
				>
					<Spaced>
						<NameField defaultName={calendarNameInForm} />
						<TimezoneField
							defaultTimeZone={defaultTimezone}
							options={allTimezones}
							selectedTimeZoneId={timezoneId}
						/>
						<WorkWeek workingTimes={workingTimes} />
						<Holidays
							list={holidaysRef.current}
							getFormValues={getFormValues}
							setHolidays={setHolidaysRef}
						/>
						{deletable && (
							<RecalculationWarning data={costData} error={costError} loading={costLoading} />
						)}
						{!loading && error && (
							<ErrorMessage>
								{getValidationErrorMessage(error) || formatMessage(messages.errorMessage)}
							</ErrorMessage>
						)}
						<Footer type="submit" onCancel={onCancel} onSave={noop} isLoading={loading} />
						{(isFormDirty || dirty) && (
							<NavBlocker
								onDiscard={onCancelWithNextCalendarActionCallback}
								onSave={saveMetricFromBlockerModal}
							/>
						)}
					</Spaced>
				</form>
			)}
		</AkForm>
	);
};

export const CalendarForm = injectIntl(CalendarFormView);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Spaced = styled.div({
	padding: token('space.200', '16px'),
	border: `1px solid ${token('color.border.focused', B200)}`,
});
